export type Operador = '+' | '-' | '*' | '/'

export type Token =
    | { tipo: 'numero'; valor: number }
    | { tipo: 'operador'; valor: Operador }
    | { tipo: 'parentese'; valor: '(' | ')' }

/** Erro de sintaxe da expressão — mensagem exibida direto no display. */
export class ErroExpressao extends Error {}

const isDigito = (caractere: string) => caractere >= '0' && caractere <= '9'

const isOperador = (caractere: string): caractere is Operador =>
    caractere === '+' ||
    caractere === '-' ||
    caractere === '*' ||
    caractere === '/'

/**
 * Um menos é unário quando não há operando antes dele:
 * início da expressão, depois de outro operador ou depois de '('.
 */
const isMenosUnario = (tokens: Token[]) => {
    const anterior = tokens.at(-1)
    if (!anterior) return true
    if (anterior.tipo === 'operador') return true
    return anterior.tipo === 'parentese' && anterior.valor === '('
}

/**
 * Quebra a expressão em tokens, convertendo `HH:mm` em minutos.
 *
 * Um menos unário vira `( 0 - x )` — não apenas `0 - x` — para não
 * competir por precedência/associatividade com o operador anterior.
 * Sem os parênteses, `2 * -3` desaçucararia para `2 * 0 - 3` (= -3,
 * errado; o certo é -6). O fechamento de cada parêntese sintético é
 * adiado até o fim do operando (`fecharPendentes`), e a contagem de
 * pendências é salva/restaurada por nível ao entrar/sair de um `(`
 * explícito, já que cada nível de parênteses tem seu próprio operando.
 *
 * @example tokenizar('02:00 + 120') // 120, '+', 120
 * @example tokenizar('2 * -3') // 2, '*', '(', 0, '-', 3, ')'
 */
export const tokenizar = (entrada: string): Token[] => {
    const tokens: Token[] = []
    let posicao = 0

    let pendentes = 0 // unários aguardando o fim do operando
    const pendentesPorNivel: number[] = []

    const fecharPendentes = () => {
        while (pendentes > 0) {
            tokens.push({ tipo: 'parentese', valor: ')' })
            pendentes--
        }
    }

    while (posicao < entrada.length) {
        const caractere = entrada[posicao]

        if (caractere === ' ') {
            posicao++
            continue
        }

        if (isDigito(caractere)) {
            let digitos = ''
            while (posicao < entrada.length && isDigito(entrada[posicao]))
                digitos += entrada[posicao++]

            if (entrada[posicao] !== ':') {
                tokens.push({ tipo: 'numero', valor: Number(digitos) })
                fecharPendentes()
                continue
            }

            posicao++
            let minutos = ''
            while (posicao < entrada.length && isDigito(entrada[posicao]))
                minutos += entrada[posicao++]

            if (minutos === '')
                throw new ErroExpressao('Faltam os minutos depois de ":"')

            tokens.push({
                tipo: 'numero',
                valor: Number(digitos) * 60 + Number(minutos),
            })
            fecharPendentes()
            continue
        }

        if (isOperador(caractere)) {
            if (caractere === '-' && isMenosUnario(tokens)) {
                tokens.push({ tipo: 'parentese', valor: '(' })
                tokens.push({ tipo: 'numero', valor: 0 })
                pendentes++
                tokens.push({ tipo: 'operador', valor: caractere })
                posicao++
                continue
            }

            tokens.push({ tipo: 'operador', valor: caractere })
            posicao++
            continue
        }

        if (caractere === '(') {
            tokens.push({ tipo: 'parentese', valor: caractere })
            pendentesPorNivel.push(pendentes)
            pendentes = 0
            posicao++
            continue
        }

        if (caractere === ')') {
            fecharPendentes() // fecha o nível corrente primeiro
            tokens.push({ tipo: 'parentese', valor: caractere })
            pendentes = pendentesPorNivel.pop() ?? 0
            fecharPendentes()
            posicao++
            continue
        }

        throw new ErroExpressao(`Caractere inválido: "${caractere}"`)
    }

    fecharPendentes()

    return tokens
}

export type ResultadoExpressao =
    | { ok: true; minutos: number }
    | { ok: false; erro: string }

const PRECEDENCIA: Record<Operador, number> = {
    '+': 1,
    '-': 1,
    '*': 2,
    '/': 2,
}

/** Só '(' chega à pilha — ')' é consumido na hora. */
type TokenPilha =
    | { tipo: 'operador'; valor: Operador }
    | { tipo: 'parentese'; valor: '(' }
type TokenRPN = Extract<Token, { tipo: 'numero' | 'operador' }>

/** Shunting-yard: infixo → notação polonesa reversa. */
const paraRPN = (tokens: Token[]): TokenRPN[] => {
    const saida: TokenRPN[] = []
    const pilha: TokenPilha[] = []

    for (const token of tokens) {
        if (token.tipo === 'numero') {
            saida.push(token)
            continue
        }

        if (token.tipo === 'operador') {
            while (pilha.length) {
                const topo = pilha[pilha.length - 1]
                if (topo.tipo !== 'operador') break
                if (PRECEDENCIA[topo.valor] < PRECEDENCIA[token.valor]) break
                saida.push(topo)
                pilha.pop()
            }
            pilha.push(token)
            continue
        }

        if (token.valor === '(') {
            pilha.push({ tipo: 'parentese', valor: '(' })
            continue
        }

        let achouAbertura = false
        for (let topo = pilha.pop(); topo; topo = pilha.pop()) {
            if (topo.tipo === 'parentese') {
                achouAbertura = true
                break
            }
            saida.push(topo)
        }

        if (!achouAbertura) throw new ErroExpressao('Parênteses não fecham')
    }

    for (let topo = pilha.pop(); topo; topo = pilha.pop()) {
        if (topo.tipo === 'parentese')
            throw new ErroExpressao('Parênteses não fecham')
        saida.push(topo)
    }

    return saida
}

const aplicar = (operador: Operador, esquerda: number, direita: number) => {
    switch (operador) {
        case '+':
            return esquerda + direita
        case '-':
            return esquerda - direita
        case '*':
            return esquerda * direita
        case '/':
            if (direita === 0) throw new ErroExpressao('Divisão por zero')
            return esquerda / direita
    }
}

const avaliarRPN = (rpn: TokenRPN[]): number => {
    const pilha: number[] = []

    for (const token of rpn) {
        if (token.tipo === 'numero') {
            pilha.push(token.valor)
            continue
        }

        const direita = pilha.pop()
        const esquerda = pilha.pop()

        if (direita === undefined || esquerda === undefined)
            throw new ErroExpressao('Expressão incompleta')

        pilha.push(aplicar(token.valor, esquerda, direita))
    }

    if (pilha.length > 1)
        throw new ErroExpressao('Falta um operador entre os valores')
    if (pilha.length !== 1) throw new ErroExpressao('Expressão incompleta')

    if (!Number.isFinite(pilha[0]))
        throw new ErroExpressao('Resultado fora do limite')

    return pilha[0]
}

/**
 * Avalia a expressão inteira em minutos.
 *
 * Nunca lança: erro de sintaxe volta como `{ ok: false, erro }`
 * para o display mostrar direto.
 *
 * @example avaliar('02:00 + 120') // { ok: true, minutos: 240 }
 */
export const avaliar = (entrada: string): ResultadoExpressao => {
    try {
        const tokens = tokenizar(entrada)

        if (!tokens.length) throw new ErroExpressao('Expressão vazia')

        return { ok: true, minutos: avaliarRPN(paraRPN(tokens)) }
    } catch (erro) {
        if (erro instanceof ErroExpressao)
            return { ok: false, erro: erro.message }
        throw erro
    }
}
